import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  BadgeCheck,
  Sparkles,
  LineChart as LineChartIcon,
  FileSignature,
  Receipt,
  Scale,
  ShoppingBag,
  CreditCard,
  ScanLine,
  Layers,
  ShieldCheck,
  Wallet,
  PiggyBank,
  MapPin,
  Zap,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import SiteFooter from "@/components/SiteFooter";
import { CFO_STACK, flatCategories } from "@/data/cfoStack";
import CASES from "@/data/caseStudies";
import { industryLabel } from "@/data/taxonomy";

const ICONS = {
  Sparkles,
  LineChart: LineChartIcon,
  FileSignature,
  Receipt,
  Scale,
  ShoppingBag,
  CreditCard,
  ScanLine,
  Layers,
  ShieldCheck,
  Wallet,
  PiggyBank,
};

const accentMap = {
  emerald: {
    ring: "border-emerald-500",
    softBg: "bg-emerald-50",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
  },
  amber: {
    ring: "border-amber-500",
    softBg: "bg-amber-50",
    text: "text-amber-700",
    dot: "bg-amber-500",
  },
  sky: {
    ring: "border-sky-500",
    softBg: "bg-sky-50",
    text: "text-sky-700",
    dot: "bg-sky-500",
  },
};

const readMember = () => {
  try {
    const raw = localStorage.getItem("deployd_member");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export default function CFOStack() {
  const [member, setMember] = useState(() => readMember());
  const [selectedKey, setSelectedKey] = useState(() => {
    // Default to first category that has cases
    const flat = flatCategories();
    return (flat.find((c) => c.cases.length > 0) || flat[0]).key;
  });
  const panelRef = useRef(null);

  useEffect(() => {
    setMember(readMember());
  }, []);

  const industry = member?.industry || "";
  const readableIndustry = industry ? industryLabel(industry) : "";

  const selected = useMemo(
    () => flatCategories().find((c) => c.key === selectedKey),
    [selectedKey]
  );

  const orderedCases = useMemo(() => {
    if (!selected) return [];
    const list = selected.cases
      .map((id) => CASES.find((c) => c.id === id))
      .filter(Boolean);
    if (!industry) return list;
    return [...list].sort((a, b) => {
      const aMatch = a.industryKey === industry ? -1 : 0;
      const bMatch = b.industryKey === industry ? -1 : 0;
      return aMatch - bMatch;
    });
  }, [selected, industry]);

  const onSelect = (key) => {
    setSelectedKey(key);
    // Smoothly bring the panel into view
    requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <main
      data-testid="cfo-stack-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#F7F6F1] text-zinc-900"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 8%, rgba(22,163,74,0.10), transparent 42%), radial-gradient(circle at 85% 92%, rgba(22,163,74,0.06), transparent 45%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,10,10,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(10,10,10,0.06) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 85%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 40%, transparent 85%)",
        }}
      />
      <div className="grain-overlay" />

      {/* Top bar */}
      <header className="relative z-10 flex items-center justify-between border-b border-zinc-200 px-6 py-4 sm:px-10 lg:px-16">
        <Link
          to="/"
          data-testid="stack-back-home"
          className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-emerald-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <BrandMark size="inline" /> / stack
        </Link>
        {member?.full_name && (
          <span
            data-testid="stack-member-tag"
            className="hidden items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500 sm:inline-flex"
          >
            <MapPin className="h-3 w-3 text-emerald-600" />
            Signed in as {member.full_name}
            {readableIndustry && (
              <>
                <span className="text-zinc-300">·</span>
                <span className="text-emerald-700">{readableIndustry} pinned</span>
              </>
            )}
          </span>
        )}
      </header>

      {/* Intro */}
      <section className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-6 pt-12 sm:px-10 sm:pt-16 lg:px-16">
        <p className="font-mono-tech text-[11px] uppercase tracking-[0.32em] text-emerald-600">
          // the cfo&apos;s office &middot; mapped
        </p>
        <h1
          data-testid="stack-title"
          className="mt-3 max-w-4xl font-display text-[clamp(2rem,4.5vw,3.75rem)] font-extrabold leading-[1.02] tracking-tight text-zinc-900"
        >
          Every layer of the CFO&apos;s stack.{" "}
          <span className="text-emerald-600">Every verified use case.</span>
        </h1>
        <p className="mt-5 max-w-3xl text-base leading-relaxed text-zinc-700 sm:text-lg">
          Click any tile to see what&apos;s actually deployed in that layer.
          {readableIndustry ? (
            <>
              {" "}Cases from <span className="font-bold text-emerald-700">{readableIndustry}</span> are pinned to the top for you.
            </>
          ) : (
            " Sign up to pin your industry to the top of every list."
          )}
        </p>
      </section>

      {/* Stack tiers */}
      <section
        data-testid="stack-tiers"
        className="relative z-10 mx-auto mt-10 w-full max-w-7xl px-6 pb-10 sm:px-10 lg:px-16"
      >
        <div className="flex flex-col gap-4">
          {CFO_STACK.map((tier, tIdx) => (
            <div
              key={tier.tierKey}
              data-testid={`tier-${tier.tierKey}`}
              className="relative border border-zinc-300 bg-white/85 backdrop-blur-sm"
              style={{ animation: `tierIn 0.6s ${tIdx * 90}ms both ease-out` }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr]">
                {/* Left rail — tier label */}
                <div className="flex items-center justify-between border-b border-zinc-200 bg-zinc-50 px-5 py-4 lg:flex-col lg:items-start lg:justify-center lg:border-b-0 lg:border-r lg:px-6 lg:py-6">
                  <div>
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-emerald-600">
                      Layer {tIdx + 1} / {CFO_STACK.length}
                    </p>
                    <p className="mt-1 font-display text-lg font-extrabold tracking-tight text-zinc-900 sm:text-xl">
                      {tier.tierLabel}
                    </p>
                    <p className="mt-1 hidden font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500 lg:block">
                      {tier.tierBlurb}
                    </p>
                  </div>
                </div>

                {/* Category tiles */}
                <div className="grid grid-cols-1 gap-px bg-zinc-200 sm:grid-cols-2 lg:grid-cols-3">
                  {tier.categories.map((cat) => {
                    const Icon = ICONS[cat.icon] || Sparkles;
                    const accent = accentMap[cat.accent] || accentMap.emerald;
                    const isSelected = selectedKey === cat.key;
                    const hasCases = cat.cases.length > 0;
                    // Compute whether industry has a match in this category
                    const industryHits = industry
                      ? cat.cases.filter((id) => {
                          const c = CASES.find((x) => x.id === id);
                          return c && c.industryKey === industry;
                        }).length
                      : 0;
                    return (
                      <button
                        key={cat.key}
                        type="button"
                        data-testid={`stack-tile-${cat.key}`}
                        onClick={() => onSelect(cat.key)}
                        className={`group relative flex h-full flex-col items-start gap-3 bg-white p-5 text-left transition-colors sm:p-6 ${
                          isSelected
                            ? `${accent.ring} border-l-2`
                            : "border-l-2 border-transparent hover:bg-zinc-50"
                        }`}
                      >
                        <div className="flex w-full items-center justify-between gap-3">
                          <div
                            className={`inline-flex h-9 w-9 items-center justify-center border ${accent.ring} ${accent.softBg} ${accent.text}`}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex items-center gap-2">
                            {industryHits > 0 && (
                              <span
                                data-testid={`industry-hits-${cat.key}`}
                                className="inline-flex items-center gap-1 border border-emerald-500 bg-emerald-50 px-1.5 py-0.5 font-mono-tech text-[9px] uppercase tracking-[0.22em] text-emerald-700"
                              >
                                <BadgeCheck className="h-3 w-3" />
                                {industryHits} in {readableIndustry}
                              </span>
                            )}
                            <span
                              className={`inline-flex items-center gap-1 font-mono-tech text-[9px] uppercase tracking-[0.24em] ${
                                hasCases ? "text-zinc-500" : "text-zinc-400"
                              }`}
                            >
                              {hasCases ? `${cat.cases.length} cases` : "Curating"}
                              {hasCases && (
                                <ArrowUpRight
                                  className={`h-3 w-3 transition-transform group-hover:translate-x-0.5 ${accent.text}`}
                                />
                              )}
                            </span>
                          </div>
                        </div>

                        <p
                          className={`font-display text-lg font-extrabold leading-tight tracking-tight text-zinc-900 ${
                            isSelected ? accent.text : ""
                          } sm:text-xl`}
                        >
                          {cat.label}
                        </p>
                        <p className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                          {cat.blurb}
                        </p>

                        {isSelected && (
                          <span
                            aria-hidden
                            className={`absolute -bottom-px left-0 h-[2px] w-full ${accent.dot}`}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Selected category panel */}
      <section
        ref={panelRef}
        data-testid="stack-panel"
        className="relative z-10 border-t border-zinc-200 bg-white/70 py-14 sm:py-16"
      >
        <div className="mx-auto w-full max-w-7xl px-6 sm:px-10 lg:px-16">
          {selected && (
            <>
              <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
                <div>
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-emerald-600">
                    // {selected.tierLabel} &middot; showing
                  </p>
                  <h2 className="mt-2 font-display text-3xl font-extrabold leading-tight tracking-tight text-zinc-900 sm:text-4xl">
                    {selected.label}
                  </h2>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-zinc-700 sm:text-base">
                    {selected.blurb}
                  </p>
                </div>
                <span className="inline-flex items-center gap-2 border border-zinc-300 bg-white px-3 py-2 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500">
                  <Zap className="h-3.5 w-3.5 text-emerald-600" />
                  {orderedCases.length} verified {orderedCases.length === 1 ? "case" : "cases"}
                </span>
              </div>

              {orderedCases.length === 0 ? (
                <div className="mt-10 border border-dashed border-zinc-300 bg-white/60 p-10 text-center">
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-emerald-600">
                    // curating
                  </p>
                  <p className="mt-3 font-display text-xl font-bold text-zinc-900 sm:text-2xl">
                    First verified cases landing shortly.
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-600">
                    We&apos;re actively adding verified deployments in this layer.
                    Check back or contribute a case from your dashboard.
                  </p>
                  <Link
                    to="/dashboard"
                    data-testid="stack-contribute-cta"
                    className="mt-6 inline-flex items-center gap-2 border border-zinc-900 bg-zinc-900 px-5 py-3 font-mono-tech text-[11px] uppercase tracking-[0.24em] text-white hover:border-emerald-600 hover:bg-emerald-600"
                  >
                    Contribute a case <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              ) : (
                <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                  {orderedCases.map((c) => {
                    const industryMatch = industry && c.industryKey === industry;
                    return (
                      <Link
                        key={c.id}
                        to={`/case-study/${c.id}`}
                        data-testid={`stack-case-${c.id}`}
                        className={`group flex h-full flex-col gap-3 border bg-white p-5 no-underline transition-colors sm:p-6 ${
                          industryMatch
                            ? "border-emerald-500 shadow-[0_1px_0_0_rgba(16,185,129,0.15),0_24px_60px_-30px_rgba(16,185,129,0.35)]"
                            : "border-zinc-200 hover:border-emerald-500"
                        }`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1 border border-zinc-200 bg-white px-2 py-1 font-mono-tech text-[9px] uppercase tracking-[0.22em] text-zinc-600">
                            {c.industry}
                          </span>
                          {industryMatch && (
                            <span
                              data-testid={`industry-pinned-${c.id}`}
                              className="inline-flex items-center gap-1 border border-emerald-500 bg-emerald-50 px-2 py-1 font-mono-tech text-[9px] uppercase tracking-[0.22em] text-emerald-700"
                            >
                              <BadgeCheck className="h-3 w-3" />
                              In your industry
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-zinc-500">
                            {c.vendor} <span className="text-zinc-300">·</span> at
                          </p>
                          <p className="mt-1 font-display text-xl font-extrabold leading-tight tracking-tight text-zinc-900 group-hover:text-emerald-700 sm:text-2xl">
                            {c.customer}
                          </p>
                        </div>
                        <div className="border-t border-zinc-200 pt-4">
                          <p className="font-display text-3xl font-black tracking-tight text-emerald-700">
                            {c.metric}
                          </p>
                          <p className="mt-1 font-mono-tech text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                            {c.metricLabel}
                          </p>
                        </div>
                        <p className="mt-auto text-sm leading-relaxed text-zinc-700">
                          {c.summary}
                        </p>
                        <span className="mt-2 inline-flex items-center gap-1 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-zinc-500 group-hover:text-emerald-700">
                          Open case study{" "}
                          <ArrowUpRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                        </span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <SiteFooter />

      <style>{`
        @keyframes tierIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </main>
  );
}
